import React, { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, X, Search } from "lucide-react";
import { api } from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import { fmtPrice, FEATURES } from "@/lib/company";
import { usePageMeta } from "@/lib/seo";

const SORTS = [
  ["new", "Neueste zuerst"],
  ["price_asc", "Preis aufsteigend"],
  ["price_desc", "Preis absteigend"],
  ["km_asc", "Kilometer aufsteigend"],
  ["power_desc", "Leistung absteigend"],
];

const EMPTY = { q: "", brand: "", body_type: "", fuel: "", transmission: "", price_max: "", km_max: "", year_min: "", power_min: "" };
const selCls = "bg-elevated border border-line text-ink rounded-lg px-4 py-3 text-sm outline-none focus:border-brand cursor-pointer";

const uniq = (list, key) => [...new Set(list.map((v) => v[key]).filter(Boolean))].sort();

const FilterPanel = ({ f, setF, all }) => {
  const sel = (key, placeholder, options, testId) => (
    <select data-testid={testId} value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} className={selCls}>
      <option value="">{placeholder}</option>
      {options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
    </select>
  );
  const pairs = (arr) => arr.map((x) => [x, x]);
  return (
    <div data-testid="filter-panel" className="rounded-xl border border-line bg-surface p-5 md:p-6 mb-10 fade-up grid grid-cols-2 md:grid-cols-4 gap-3">
      {sel("brand", "Alle Marken", pairs(uniq(all, "brand")), "filter-brand")}
      {sel("body_type", "Alle Karosserien", pairs(uniq(all, "body_type")), "filter-body")}
      {sel("fuel", "Alle Kraftstoffe", pairs(uniq(all, "fuel")), "filter-fuel")}
      {sel("transmission", "Alle Getriebe", pairs(uniq(all, "transmission")), "filter-transmission")}
      {sel("price_max", "Preis bis", [25000, 50000, 75000, 100000, 150000, 250000].map((p) => [p, fmtPrice(p)]), "filter-price")}
      {sel("km_max", "Kilometer bis", [10000, 30000, 60000, 100000, 150000].map((k) => [k, `${k.toLocaleString("de-AT")} km`]), "filter-km")}
      {sel("year_min", "Baujahr ab", [2005, 2010, 2015, 2018, 2020, 2022, 2024].map((y) => [y, y]), "filter-year")}
      {sel("power_min", "Leistung ab", [150, 250, 350, 450, 550].map((p) => [p, `${p} PS`]), "filter-power")}
    </div>
  );
};

const applyFilters = (all, f, sort) => {
  const q = f.q.trim().toLowerCase();
  const list = all.filter((v) => {
    if (q && !`${v.brand} ${v.model} ${v.title}`.toLowerCase().includes(q)) return false;
    if (f.brand && v.brand !== f.brand) return false;
    if (f.body_type && v.body_type !== f.body_type) return false;
    if (f.fuel && v.fuel !== f.fuel) return false;
    if (f.transmission && v.transmission !== f.transmission) return false;
    if (f.price_max && v.price > +f.price_max) return false;
    if (f.km_max && v.mileage > +f.km_max) return false;
    if (f.year_min && (v.year || 0) < +f.year_min) return false;
    if (f.power_min && (v.power_ps || 0) < +f.power_min) return false;
    return true;
  });
  const sorters = {
    new: (a, b) => (b.is_new_arrival - a.is_new_arrival) || (b.first_seen_at || b.created_at || "").localeCompare(a.first_seen_at || a.created_at || "") || (b.year || 0) - (a.year || 0),
    price_asc: (a, b) => a.price - b.price,
    price_desc: (a, b) => b.price - a.price,
    km_asc: (a, b) => a.mileage - b.mileage,
    power_desc: (a, b) => (b.power_ps || 0) - (a.power_ps || 0),
  };
  return [...list].sort(sorters[sort] || sorters.new);
};

export default function Vehicles() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [sort, setSort] = useState("new");
  usePageMeta("Fahrzeugbestand", "Aktueller Fahrzeugbestand von AD MOTORS in Münchendorf – Sportwagen & Premiumfahrzeuge.");

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setAll(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => applyFilters(all, f, sort), [all, f, sort]);
  const active = Object.entries(f).filter(([k, v]) => k !== "q" && v !== "").length;

  return (
    <div data-testid="vehicles-page" className="pt-28 md:pt-36 pb-24 max-w-7xl mx-auto px-5 sm:px-8">
      <p className="eyebrow">Fahrzeugbestand</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-ink mt-5 mb-12 tracking-tight">Unsere Fahrzeuge</h1>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <label className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-faint" />
          <input data-testid="vehicles-search-input" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
            placeholder="Marke oder Modell suchen" className={`${selCls} w-full pl-10 cursor-text`} />
        </label>
        <button data-testid="filter-toggle-btn" onClick={() => setShowFilters(!showFilters)}
          className={`btn-pill inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold border ${showFilters || active ? "bg-brand text-brand-ink border-brand" : "border-line text-ink hover:border-silver"}`}>
          <SlidersHorizontal size={15} /> Filter {active > 0 && `(${active})`}
        </button>
        {active > 0 && (
          <button data-testid="filter-reset-btn" onClick={() => setF({ ...EMPTY, q: f.q })}
            className="inline-flex items-center gap-1.5 text-sm text-dim hover:text-ink transition-colors"><X size={14} /> Zurücksetzen</button>
        )}
        <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className={`${selCls} md:ml-auto`}>
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <p className="text-sm text-faint w-full md:w-auto" data-testid="vehicles-count">
          {filtered.length} {filtered.length === 1 ? "Fahrzeug" : "Fahrzeuge"}
        </p>
      </div>

      {showFilters && <FilterPanel f={f} setF={setF} all={all} />}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => <div key={i} className="animate-pulse rounded-2xl bg-surface border border-line aspect-[4/5]" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="vehicles-grid">
          {filtered.map((v) => <VehicleCard key={v.id} vehicle={v} />)}
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-24" data-testid="vehicles-empty">
          <p className="font-display text-xl font-bold text-ink mb-2">Keine Fahrzeuge gefunden</p>
          <p className="text-sm text-dim">Bitte passen Sie Ihre Suche an{FEATURES.findet ? " – oder fragen Sie uns nach Ihrem Wunschfahrzeug" : ""}.</p>
        </div>
      )}
    </div>
  );
}
